import type { MouseEvent } from 'react';

import type { GetLsproBrandingSettingsQuery, ThemeFragmentFragment } from '../../../generated/graphql-manager';

import {
  AlertButton,
  IconButton,
  Modal,
  PencilIcon,
  SecondaryButton,
  Tooltip,
  TrashIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GET_LSPRO_BRANDING_SETTINGS } from './branding.api';
import { useDeleteThemeMutation } from '../../../generated/graphql-manager';
import { useCurrentUser } from '../../hooks/current-user';

import styles from './branding.module.css';

interface ThemeListItemProps {
  theme: ThemeFragmentFragment;
  onClick: (themeId: string) => void;
}

export function ThemeListItem({ theme, onClick }: ThemeListItemProps) {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const [deleteTheme] = useDeleteThemeMutation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteTheme = () => {
    deleteTheme({
      variables: {
        input: { lsproId, themeId: theme.id },
      },
      optimisticResponse: {
        deleteTheme: {
          __typename: 'DeleteThemeSuccess',
          themeId: theme.id,
        },
      },
      update: (cache, { data }) => {
        if (data?.deleteTheme.__typename !== 'DeleteThemeSuccess') return;

        const cachedData = cache.readQuery<GetLsproBrandingSettingsQuery>({
          query: GET_LSPRO_BRANDING_SETTINGS,
          variables: { lsproId },
        });

        if (cachedData?.lsproSettings?.themes) {
          cache.writeQuery({
            query: GET_LSPRO_BRANDING_SETTINGS,
            variables: { lsproId },
            data: {
              ...cachedData,
              lsproSettings: {
                ...cachedData.lsproSettings,
                themes: cachedData.lsproSettings.themes.filter((t) => t.id !== theme.id),
              },
            },
          });
        }
      },
      onCompleted: () => setIsDeleteModalOpen(false),
      onError: () => setIsDeleteModalOpen(false),
    });
  };

  return (
    <div
      role="button"
      className={styles.theme}
      data-testid={`theme-${theme.id}`}
      onClick={(event) => event.target === event.currentTarget && onClick(theme.id)}
    >
      <span>{theme.name}</span>

      <Tooltip label={t('manager.branding.themeList.editThemeTooltip')}>
        <IconButton
          data-testid={`edit-theme-button-${theme.id}`}
          small
          rounded
          aria-label={t('manager.branding.themeList.editThemeTooltip')}
          onClick={() => onClick(theme.id)}
        >
          <PencilIcon />
        </IconButton>
      </Tooltip>

      <Tooltip
        label={
          theme.isDefault
            ? t('manager.branding.themeList.defaultThemeTooltip')
            : t('manager.branding.themeList.deleteButtonTooltip')
        }
      >
        <IconButton
          data-testid={`delete-theme-button-${theme.id}`}
          small
          rounded
          disabled={theme.isDefault}
          aria-label={t('manager.branding.themeList.deleteButtonTooltip')}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            setIsDeleteModalOpen(true);
          }}
        >
          <TrashIcon />
        </IconButton>
      </Tooltip>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        data-testid={`delete-theme-modal-${theme.id}`}
        title={t('manager.branding.themeList.deleteModal.title')}
        className={styles.themeModal}
        footer={
          <>
            <SecondaryButton
              data-testid={`delete-theme-modal-cancel-button-${theme.id}`}
              onClick={() => setIsDeleteModalOpen(false)}
            >
              {t('common.cancel')}
            </SecondaryButton>

            <AlertButton data-testid={`delete-theme-modal-confirm-button-${theme.id}`} onClick={handleDeleteTheme}>
              {t('manager.branding.themeList.deleteModal.confirmButton')}
            </AlertButton>
          </>
        }
      >
        {t('manager.branding.themeList.deleteModal.description')}
      </Modal>
    </div>
  );
}
